// components/workspace-timeline/useTimelineExportRealtimeStatus.ts
import type { RefObject } from 'react';
import { TIMELINE_EXPORT_STATUS_EVENT } from '#lib/socket-events';
import type {
  TimelineCompositionStatus,
  TimelineExportStatusSocketPayload,
} from '#lib/timeline-video-types';
import type { KernelHandle, SocketStatus } from '#types/types';

type TimelineCompositionStatusEnvelope = {
  cacheKey?: string;
  status?: TimelineCompositionStatus;
  cached?: boolean;
};

type WaitForTimelineExportReadyViaSocketParams = {
  kernel: RefObject<KernelHandle | null>;
  cacheKey: string;
  initialStatus?: TimelineCompositionStatus | null;
  timeoutMs: number;
  abortSignal?: AbortSignal;
  onStatus?: (status: TimelineCompositionStatus) => void;
  fallbackStatusUrl?: string;
  socketSilenceMs?: number;
  socketEnabled?: boolean;
};

const DEFAULT_SOCKET_SILENCE_MS = 4_000;
const FALLBACK_INITIAL_DELAY_MS = 4_000;
const FALLBACK_MAX_DELAY_MS = 15_000;

const createAbortError = (): Error => {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('Aborted', 'AbortError');
  }
  const error = new Error('AbortError');
  (error as { name?: string }).name = 'AbortError';
  return error;
};

const waitWithAbort = (ms: number, abortSignal?: AbortSignal): Promise<void> => {
  if (abortSignal?.aborted) {
    return Promise.reject(createAbortError());
  }

  return new Promise<void>((resolve, reject) => {
    let timeoutId = 0;
    const cleanup = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      abortSignal?.removeEventListener('abort', handleAbort);
    };
    const handleAbort = () => {
      cleanup();
      reject(createAbortError());
    };
    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    abortSignal?.addEventListener('abort', handleAbort, { once: true });
  });
};

const isTerminalStatus = (status?: TimelineCompositionStatus | null): boolean => (
  status?.status === 'ready' || status?.status === 'error' || status?.status === 'missing'
);

const assertTerminalStatus = (status: TimelineCompositionStatus): TimelineCompositionStatus => {
  if (status.status === 'ready') return status;
  if (status.status === 'error') {
    throw new Error(status.error || 'Timeline export failed during composition.');
  }
  if (status.status === 'missing') {
    throw new Error(status.error || 'Timeline export status is missing.');
  }
  return status;
};

const isTimelineExportStatusPayload = (
  payload: unknown,
  cacheKey: string
): payload is TimelineExportStatusSocketPayload => {
  if (!payload || typeof payload !== 'object') return false;
  const record = payload as Record<string, unknown>;
  if (record.cacheKey !== cacheKey) return false;
  const status = record.status;
  if (!status || typeof status !== 'object') return false;
  const statusRecord = status as Record<string, unknown>;
  return statusRecord.cacheKey === cacheKey && typeof statusRecord.status === 'string';
};

const pollTimelineExportStatusFallback = async ({
  cacheKey,
  statusUrl,
  timeoutAt,
  abortSignal,
  onStatus,
  initialDelayMs = FALLBACK_INITIAL_DELAY_MS,
}: {
  cacheKey: string;
  statusUrl: string;
  timeoutAt: number;
  abortSignal?: AbortSignal;
  onStatus?: (status: TimelineCompositionStatus) => void;
  initialDelayMs?: number;
}): Promise<TimelineCompositionStatus> => {
  let delayMs = Math.max(0, initialDelayMs);

  for (;;) {
    if (Date.now() > timeoutAt) {
      throw new Error('Timed out waiting for timeline composition worker.');
    }
    if (delayMs > 0) {
      await waitWithAbort(delayMs, abortSignal);
    }

    const response = await fetch(statusUrl, { method: 'GET', signal: abortSignal });
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Timeline export status failed with status ${response.status}.`);
    }

    const envelope = (await response.json()) as TimelineCompositionStatusEnvelope;
    const statusPayload = envelope.status ?? null;
    if (!statusPayload || statusPayload.cacheKey !== cacheKey) {
      throw new Error('Timeline export status response did not match the requested cache key.');
    }

    onStatus?.(statusPayload);
    if (isTerminalStatus(statusPayload)) {
      return assertTerminalStatus(statusPayload);
    }

    delayMs = Math.min(FALLBACK_MAX_DELAY_MS, Math.max(FALLBACK_INITIAL_DELAY_MS, Math.round(delayMs * 1.6)));
  }
};

export const waitForTimelineExportReadyViaSocket = ({
  kernel,
  cacheKey,
  initialStatus,
  timeoutMs,
  abortSignal,
  onStatus,
  fallbackStatusUrl,
  socketSilenceMs = DEFAULT_SOCKET_SILENCE_MS,
  socketEnabled = true,
}: WaitForTimelineExportReadyViaSocketParams): Promise<TimelineCompositionStatus> => {
  const normalizedCacheKey = cacheKey.trim();
  if (!normalizedCacheKey) {
    return Promise.reject(new Error('Timeline export cache key is required.'));
  }

  if (initialStatus) {
    onStatus?.(initialStatus);
    if (isTerminalStatus(initialStatus)) {
      try {
        return Promise.resolve(assertTerminalStatus(initialStatus));
      } catch (error) {
        return Promise.reject(error);
      }
    }
  }

  if (abortSignal?.aborted) {
    return Promise.reject(createAbortError());
  }

  const statusUrl = fallbackStatusUrl ?? `/api/export/timeline-video?mode=status&cacheKey=${encodeURIComponent(normalizedCacheKey)}`;
  const timeoutAt = Date.now() + Math.max(1_000, timeoutMs);
  const socketStatus: SocketStatus | undefined = kernel.current?.getSocketStatus?.();

  return new Promise<TimelineCompositionStatus>((resolve, reject) => {
    let settled = false;
    let sawSocketEvent = false;
    let fallbackStarted = false;
    let silenceTimeoutId = 0;
    let globalTimeoutId = 0;
    let unsubscribe: (() => void) | null = null;
    let fallbackAbortController: AbortController | null = null;

    const cleanup = () => {
      if (silenceTimeoutId) window.clearTimeout(silenceTimeoutId);
      if (globalTimeoutId) window.clearTimeout(globalTimeoutId);
      abortSignal?.removeEventListener('abort', handleAbort);
      unsubscribe?.();
      unsubscribe = null;
      fallbackAbortController?.abort();
      fallbackAbortController = null;
    };

    const finishResolve = (status: TimelineCompositionStatus) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(status);
    };

    const finishReject = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const startFallback = (reason: string) => {
      if (settled || fallbackStarted) return;
      fallbackStarted = true;
      console.warn('[TimelineExport] Falling back to status polling because socket status events are unavailable.', {
        cacheKey: normalizedCacheKey,
        reason,
      });
      fallbackAbortController = new AbortController();
      void pollTimelineExportStatusFallback({
        cacheKey: normalizedCacheKey,
        statusUrl,
        timeoutAt,
        abortSignal: fallbackAbortController.signal,
        onStatus,
        initialDelayMs: reason === 'socket-silence' ? 0 : FALLBACK_INITIAL_DELAY_MS,
      }).then(finishResolve).catch(finishReject);
    };

    function handleAbort() {
      finishReject(createAbortError());
    }

    abortSignal?.addEventListener('abort', handleAbort, { once: true });
    globalTimeoutId = window.setTimeout(() => {
      finishReject(new Error('Timed out waiting for timeline composition worker.'));
    }, Math.max(1_000, timeoutMs));

    if (!socketEnabled) {
      startFallback('realtime-disabled');
      return;
    }

    if (socketStatus !== 'connected') {
      startFallback(`socket-${socketStatus ?? 'unknown'}`);
      return;
    }

    unsubscribe = kernel.current?.subscribeToSocketEvent(TIMELINE_EXPORT_STATUS_EVENT, (payload: unknown) => {
      if (!isTimelineExportStatusPayload(payload, normalizedCacheKey)) return;
      sawSocketEvent = true;
      const statusPayload = payload.status;
      onStatus?.(statusPayload);
      if (isTerminalStatus(statusPayload)) {
        try {
          finishResolve(assertTerminalStatus(statusPayload));
        } catch (error) {
          finishReject(error);
        }
      }
    }) ?? null;

    if (!unsubscribe) {
      startFallback('subscription-unavailable');
      return;
    }

    silenceTimeoutId = window.setTimeout(() => {
      if (!sawSocketEvent) {
        startFallback('socket-silence');
      }
    }, Math.max(DEFAULT_SOCKET_SILENCE_MS, socketSilenceMs));
  });
};
